import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useNav } from '../lib/nav'
import { Page, TopBar } from '../components/Shell'
import { DoneMark, DoneDot } from '../components/DoneMark'
import { PrintCardButton } from '../components/PrintCard'
import { jobStatus } from '../lib/api'
import { useLiveRows, pollEvery } from '../lib/live'
import { fmt } from '../lib/types'
import type { RecordedState } from './IntakeReview'

type St = 'ok' | 'wait' | 'warn'
const Item = ({ st, t }: { st: St; t: string }) => (
  <div className="done__item">
    <DoneDot state={st} />
    <span>{t}</span>
  </div>
)

export function IntakeRecorded() {
  const nav = useNav()
  const s = useLocation().state as RecordedState | null
  const [cons, setCons] = useState<string>(s?.result.consignify_status ?? 'na')
  const no = s ? String(s.result.intake_no).padStart(4, '0') : ''

  // the intake row is heard live (L2) while Consignify is being written; the poll stays as the fallback, slowed while the live link is up
  const check = async () => { if (!s) return; const j = await jobStatus(s.result.intake_id).catch(() => null); if (j && j.consignify_status !== 'queued') setCons(j.consignify_status) }
  const live = useLiveRows('intakes', s && cons === 'queued' ? `id=eq.${s.result.intake_id}` : null, () => { void check() })
  useEffect(() => {
    if (!s) { nav('/intake', { replace: true }); return }
    if (cons !== 'queued') return
    let n = 0
    const t = setInterval(async () => {
      n++
      await check()
      if (n > (live ? 8 : 40)) clearInterval(t)
    }, pollEvery(live))
    return () => clearInterval(t)
  }, [s, cons, nav, live])  // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return null
  const bought = s.type === 'bought'
  const consSt: St = cons === 'assigned' || cons === 'na' ? 'ok' : cons === 'failed' ? 'warn' : 'wait'
  return (
    <Page bar={<TopBar title="Intake" />}>
      <div className="done">
        <DoneMark />
        <div className="done__title">Intake recorded</div>
        <div className="done__sub">Intake #{no} · {s.sourced ? 'Sourced by' : bought ? 'Bought from' : 'Consigned from'} {s.from} · signed by {s.by.name} ({s.by.employee_id})</div>
        <div className="card done__list done__list--in">
          {bought ? (
            <>
              <Item st={s.result.shopify_product_id ? 'ok' : 'warn'} t={s.result.shopify_product_id ? `Draft in Shopify at $0, cost ${fmt(s.cost)} recorded` : `Rehearsal: recorded here with cost ${fmt(s.cost)}, no Shopify draft`} />
              <Item st="ok" t={s.sourced ? `Cost of ${fmt(s.cost)} recorded; no payout, Shane already paid` : `Paid ${fmt(s.cost)} by ${s.payout} at intake; logged for the accountant`} />
            </>
          ) : (
            <>
              <Item st={s.result.shopify_product_id ? 'ok' : 'warn'} t={s.result.shopify_product_id ? `Draft in Shopify at $0, ${s.min > 0 ? `floor set to ${fmt(s.min)}` : 'no floor (no minimum given)'}` : `Rehearsal: recorded here ${s.min > 0 ? `with floor ${fmt(s.min)}` : 'with no floor'}, no Shopify draft`} />
              <Item st={consSt} t={cons === 'na' ? `Consignor recorded, ${s.fee}% fee (Consignify link happens at go-live)` : consSt === 'ok' ? `Assigned to the consignor in Consignify, ${s.fee}% fee saved` : consSt === 'warn' ? 'Consignify assignment failed; it will retry, and shows under Needs attention' : 'Assigning to the consignor in Consignify…'} />
            </>
          )}
          <Item st="ok" t="Intake card ready: tap Print card, then Print (5×7)" />
          <Item st="ok" t="Barcode tag prints once the listing is live" />
        </div>
        <div className="done__btns done__btns--in">
          <PrintCardButton card={s.card} />
          <button type="button" className="btn-secondary" onClick={() => nav('/intake')}>Next intake</button>
        </div>
        <button type="button" className="topbar__back" style={{ marginTop: 18, justifyContent: 'center' }} onClick={() => nav('/')}>Home</button>
        <div className="hint" style={{ textAlign: 'center', marginTop: 6 }}>Card didn't print? It's always under Intake → Recent intakes.</div>
      </div>
    </Page>
  )
}
